<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue'
import {
	TransactionType,
	RecipientType,
	DiscountType,
} from '../types.js'

import Badge from './ui/Badge.vue'
import Button from './ui/Button.vue'
import { formatDate, formatDateTime } from "@/utils/date.js";

import {
	PrinterIcon,
	DownloadIcon,
	XIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	CreditCardIcon,
	RotateCcwIcon,
} from './Icons.js'

import RefundModal from './RefundModal.vue'
import { invoicesApi } from '@js-app/office/api/invoices-api'
import { toPng } from 'html-to-image'
import jsPDF from 'jspdf'
import {
	fmt,
	getLineVat,
	getLineTotal,
	getLineTotalWithVat
} from '../utils/money'

/* ---------------- PROPS / EMITS ---------------- */
const props = defineProps({
	invoice: { type: Object, required: true },
	partnerUuid: { type: String, required: true },
	partnerToken: { type: String, required: true },
})
const emit = defineEmits(['close'])

/* ---------------- STATE ---------------- */
const currentPage = ref(1)
const isRefundModalOpen = ref(false)
const pdfRef = ref(null)

watch(
	() => props.invoice,
	() => {
		currentPage.value = 1
	},
	{ immediate: true }
)

const safeTs = (tx) => {
	const raw = tx?.timestamp || tx?.date
	const t = new Date(raw)
	return isNaN(t.getTime()) ? 0 : t.getTime()
}

const items = computed(() => props.invoice?.items || [])
const transactions = computed(() => props.invoice?.transactions || [])

const sortedTransactions = computed(() => {
	return [...transactions.value].sort((a, b) => safeTs(a) - safeTs(b))
})

const totalGoods = computed(() => props.invoice?.totals?.totalAmount || 0)
const totalPaid = computed(() => props.invoice?.totals?.paidAmount || 0)
const totalRefunded = computed(() => props.invoice?.totals?.refundAmount || 0)

const netPayment = computed(() => totalPaid.value - totalRefunded.value)
const maxRefundable = computed(() => Math.max(0, netPayment.value))
const balanceDue = computed(() => Math.max(0, totalGoods.value - totalPaid.value + totalRefunded.value))

const recipientName = computed(() => {
	const r = props.invoice?.recipient
	if (!r) return ''
	return r.type === RecipientType.INDIVIDUAL
		? `${r.person?.firstName ?? ''} ${r.person?.lastName ?? ''}`.trim()
		: (r.legal?.name || '')
})

/* ---------------- ACTIONS ---------------- */
const handlePrint = () => {
	window.print()
}

const senderDetailsLines = computed(() => {
	if (!props.invoice?.senderDetails) return []

	return props.invoice.senderDetails
		.replace(' IBAN:', '\nIBAN:')
		.split('\n')
})


const handleDownloadPdf = async () => {
	if (!pdfRef.value) return

	const dataUrl = await toPng(pdfRef.value, {
		cacheBust: true,
		pixelRatio: 2,
		skipFonts: true,
		skipAutoScale: true,
	})

	const pdf = new jsPDF('p', 'mm', 'a4')

	const imgProps = pdf.getImageProperties(dataUrl)
	const pdfWidth = pdf.internal.pageSize.getWidth()
	const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width

	pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight)
	pdf.save(`${props.invoice.number}.pdf`)
}

const handleRefund = async ({ sum, reason, items }) => {
	try {
		const invoiceId = Number(props.invoice.id)

		const payload = {
			sum,
			reason,
			sender: {
				partnerToken: props.partnerToken,
				partnerUuid: props.partnerUuid,
			},
			items: items || undefined,
			cardTransaction: {
				timestamp: new Date().toISOString().split('T')[0],
			},
		}

		await invoicesApi.refundInvoice(invoiceId, payload)

		console.log('REFUND SUCCESS')

		isRefundModalOpen.value = false
		emit('close')
	} catch (e) {
		console.error('Refund failed', e)
		alert('Refund failed')
	}
}

</script>

<template>
	<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
		<div class="bg-white w-full max-w-[1400px] h-[95vh] rounded-xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-slate-200 m-4">

			<!-- Left Pane -->
			<div class="flex-1 bg-slate-50 flex flex-col h-full border-r border-slate-200 min-w-0 relative">

				<div class="p-4 border-b border-slate-200 bg-white flex justify-between items-center shrink-0">
					<h2 class="font-semibold text-lg text-slate-800 flex items-center gap-2">
						{{ $t('invoices.view_title') }}
						<span class="text-slate-400 font-normal">#{{ invoice.number }}</span>
					</h2>

					<div class="flex gap-2">
						<Button variant="outline" size="sm" class="gap-2" @click="handlePrint">
							<PrinterIcon class="w-4 h-4" /> {{ $t('invoices.view_print') }}
						</Button>

						<Button variant="outline" size="sm" class="gap-2" @click="handleDownloadPdf">
							<DownloadIcon class="w-4 h-4" /> {{ $t('invoices.view_download') }}
						</Button>
					</div>
				</div>

				<!-- Scrollable Content -->
				<div class="flex-1 overflow-y-auto p-4 md:p-8 flex items-center justify-center relative">

					<!-- Navigation Arrows (круглі як у прототипі) -->
					<div class="absolute left-2 top-1/2 -translate-y-1/2 z-20">
						<button
							@click="currentPage = 1"
							:disabled="currentPage === 1"
							class="p-2 rounded-full shadow-md border transition-all"
							:class="currentPage === 1
								? 'bg-slate-100 text-slate-300 border-slate-200 cursor-default opacity-50'
								: 'bg-white text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-600'"
						>
							<ChevronLeftIcon class="w-6 h-6" />
						</button>
					</div>

					<div class="absolute right-2 top-1/2 -translate-y-1/2 z-20">
						<button
							@click="currentPage = 2"
							:disabled="currentPage === 2"
							class="p-2 rounded-full shadow-md border transition-all"
							:class="currentPage === 2
								? 'bg-slate-100 text-slate-300 border-slate-200 cursor-default opacity-50'
								: 'bg-white text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-600'"
						>
							<ChevronRightIcon class="w-6 h-6" />
						</button>
					</div>

					<!-- PAGE 1: Invoice -->
					<div v-if="currentPage === 1" class="bg-white shadow-sm border border-slate-200 p-10 min-h-[800px] w-full max-w-3xl mx-auto rounded-sm">
						<div class="flex justify-between items-start mb-12">
							<div>
								<div class="w-32 h-16 bg-slate-100 border border-dashed border-slate-300 rounded flex items-center justify-center text-xs text-slate-400 mb-4">
									{{ $t('invoices.logo_placeholder') }}
								</div>
								<h1 class="text-2xl font-bold text-slate-900 mb-1">{{ invoice.senderName }}</h1>
								<div class="text-sm text-slate-500">
									<div v-for="(line, i) in senderDetailsLines" :key="i">
										{{ line }}
									</div>
								</div>
							</div>

							<div class="text-right">
								<h2 class="text-3xl font-light text-slate-900 mb-4">{{ $t('invoices.invoice_title') }}</h2>
								<div class="space-y-1 text-sm text-left">
									<div class="grid grid-cols-[80px_1fr] gap-x-2">
										<span class="text-slate-500 font-medium">{{ $t('invoices.label_number') }}:</span>
										<span class="font-bold text-slate-900">{{ invoice.number }}</span>
									</div>

									<div class="grid grid-cols-[80px_1fr] gap-x-2">
										<span class="text-slate-500 font-medium">{{ $t('invoices.label_date') }}:</span>
										<span class="text-slate-900">{{ invoice.date }}</span>
									</div>

									<div class="grid grid-cols-[80px_1fr] gap-x-2">
										<span class="text-slate-500 font-medium">{{ $t('invoices.label_due_date') }}:</span>
										<span class="text-slate-900">{{ formatDate(invoice.dueDate) }}</span>
									</div>
								</div>

							</div>
						</div>

						<!-- Recipient -->
						<div class="mb-12">
							<h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
								{{ $t('invoices.bill_to') }}
							</h3>
							<div class="text-slate-900 font-bold text-lg mb-1">{{ recipientName }}</div>

							<div class="text-sm text-slate-600">
								<template v-if="invoice.recipient?.type === RecipientType.INDIVIDUAL">
									<p>{{ invoice.recipient?.person?.phone }}</p>
									<p>{{ invoice.recipient?.person?.email }}</p>
									<p>{{ invoice.recipient?.person?.address }} {{ invoice.recipient?.person?.city }}</p>
								</template>
								<template v-else>
									<p>{{ invoice.recipient?.legal?.EDRPOU }}</p>
									<p>{{ invoice.recipient?.legal?.country }}, {{ invoice.recipient?.legal?.city }}</p>
									<p>{{ invoice.recipient?.legal?.street }} {{ invoice.recipient?.legal?.building }}</p>
								</template>
							</div>
						</div>

						<!-- Items table -->
						<div class="mb-8">
							<table class="w-full text-sm">
								<thead>
									<tr class="border-b-2 border-slate-900 text-left">
										<th class="py-2 font-bold text-slate-900">{{ $t('invoices.col_description') }}</th>
										<th class="py-2 font-bold text-slate-900 text-center">{{ $t('invoices.col_qty') }}</th>
										<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.col_price') }}</th>
										<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.col_vat') }}</th>
										<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.col_total') }}</th>
									</tr>
								</thead>

								<tbody class="divide-y divide-slate-100">
									<tr v-for="it in items" :key="it.id">
										<td class="py-3 pr-4">
											<div class="font-medium text-slate-900">{{ it.name }}</div>
											<div class="text-xs text-slate-500">{{ it.extras?.code }}</div>
										</td>

										<td class="py-3 text-center">{{ it.quantity }} {{ it.extras?.unit || it.unit_code }}</td>

										<td class="py-3 text-right">{{ fmt(it.price) }}</td>

										<td class="py-3 text-right">
											{{ fmt(getLineVat(it)) }}
										</td>

										<td class="py-3 text-right font-medium">
											{{ fmt(getLineTotalWithVat(it)) }}
										</td>
									</tr>
								</tbody>

							</table>
						</div>

						<!-- Totals -->
						<div class="flex justify-end border-t border-slate-200 pt-4">
							<div class="w-72 text-right text-lg font-bold text-slate-900">
								<span>{{ $t('invoices.view_balance') }}:</span>
								<div>
									{{ fmt(balanceDue) }} {{ invoice.currency }}
								</div>
							</div>
						</div>

						<!-- Notes -->
						<div v-if="invoice.note || invoice.paymentPurpose" class="mt-12 pt-8 border-t border-slate-200">
							<h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{{ $t('invoices.notes') }}</h4>
							<p class="text-sm text-slate-600 mb-2">{{ invoice.paymentPurpose }}</p>
							<p class="text-xs text-slate-400 italic">{{ invoice.note }}</p>
						</div>

						<div class="mt-auto pt-8 text-center text-xs text-slate-300">
							{{ $t('invoices.hist_page_num') }} 1 / 2
						</div>
					</div>

					<!-- PAGE 2: Transactions history -->
					<div v-if="currentPage === 2" class="bg-white shadow-sm border border-slate-200 p-10 min-h-[800px] w-full max-w-3xl mx-auto rounded-sm flex flex-col">
						<h2 class="text-2xl font-light text-slate-900 mb-8 border-b pb-4">{{ $t('invoices.hist_page_title') }}</h2>

						<div class="flex-1">
							<table class="w-full text-sm">
								<thead>
								<tr class="border-b-2 border-slate-900 text-left">
									<th class="py-2 font-bold text-slate-900 w-24">{{ $t('invoices.hist_col_date') }}</th>
									<th class="py-2 font-bold text-slate-900">{{ $t('invoices.hist_col_type') }}</th>
									<th class="py-2 font-bold text-slate-900">{{ $t('invoices.hist_col_card') }}</th>
									<th class="py-2 font-bold text-slate-900">{{ $t('invoices.hist_col_rrn') }}</th>
									<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.hist_col_amount') }}</th>
									<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.hist_col_balance') }}</th>
								</tr>
								</thead>

								<tbody class="divide-y divide-slate-100">
								<template v-if="sortedTransactions.length">
									<tr
										v-for="(tx, idx) in sortedTransactions"
										:key="tx.id || idx"
									>
										<td class="py-3 pr-2 align-top">
											<div class="font-medium text-slate-700">{{ tx.date }}</div>
											<div class="text-[10px] text-slate-400">
												{{ tx.timestamp ? tx.timestamp.split('T')[1]?.substring(0, 5) : '' }}
											</div>
										</td>

										<td class="py-3 align-top">
											<Badge
												:variant="tx.type === TransactionType.PAYMENT ? 'success' : 'destructive'"
												class="text-[10px] px-1.5 py-0"
											>
												{{ tx.type === TransactionType.PAYMENT ? $t('invoices.trans_payment') : $t('invoices.trans_refund') }}
											</Badge>
										</td>

										<td class="py-3 align-top font-mono text-xs text-slate-600">
											{{ tx.cardMask || '-' }}
										</td>

										<td class="py-3 align-top">
											<div class="text-xs font-mono">{{ tx.rrn || tx.arn || '-' }}</div>
											<div v-if="tx.relatedItemName" class="text-[10px] text-slate-400 italic mt-1">
												{{ $t('invoices.item') }}: {{ tx.relatedItemName }}
											</div>
										</td>

										<td
											class="py-3 text-right align-top font-medium"
											:class="tx.type === TransactionType.PAYMENT ? 'text-emerald-600' : 'text-red-600'"
										>
											{{ tx.type === TransactionType.PAYMENT ? '+' : '-' }}{{ fmt(tx.amount) }}
										</td>

										<td class="py-3 text-right align-top font-bold text-slate-900">
											<!-- running balance як у React (але в копійках) -->
											{{
												(() => {
													let running = 0
													for (let i = 0; i <= idx; i++) {
														const txx = sortedTransactions[i]
														const a = Number(txx.amount) || 0
														if (txx.type === TransactionType.PAYMENT) running += a
														else running -= a
													}
													return fmt(running)
												})()
											}}
										</td>
									</tr>
								</template>

								<tr v-else>
									<td colspan="6" class="py-6 text-center text-slate-400">
										—
									</td>
								</tr>
								</tbody>
							</table>
						</div>

						<div class="mt-auto pt-8 text-center text-xs text-slate-300">
							{{ $t('invoices.hist_page_num') }} 2 / 2
						</div>
					</div>

				</div>
			</div>

			<!-- Right Pane -->
			<div class="w-full md:w-[450px] bg-white flex flex-col h-full border-l border-slate-200 shadow-xl relative z-10">
				<div class="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
					<h3 class="font-bold text-slate-800">{{ $t('invoices.view_history_title') }}</h3>
					<button
						@click="emit('close')"
						class="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded"
						aria-label="Close"
					>
						<XIcon class="w-5 h-5" />
					</button>
				</div>

				<div class="p-6 bg-slate-50/50 space-y-4">
					<!-- Summary Card -->
					<div class="bg-white rounded-lg border border-slate-200 p-4 shadow-sm space-y-3">
						<div class="flex justify-between items-center">
							<span class="text-sm text-slate-500">{{ $t('invoices.total_sum') }}</span>
							<span class="font-bold text-slate-900">{{ fmt(totalGoods) }} {{ invoice.currency }}</span>
						</div>

						<div class="flex justify-between items-center">
							<span class="text-sm text-emerald-600 font-medium">{{ $t('invoices.view_paid') }}</span>
							<span class="font-bold text-emerald-600">-{{ fmt(totalPaid) }} {{ invoice.currency }}</span>
						</div>

						<div v-if="totalRefunded > 0" class="flex justify-between items-center">
							<span class="text-sm text-red-500 font-medium">{{ $t('invoices.trans_refund') }}</span>
							<span class="font-bold text-red-500">+{{ fmt(totalRefunded) }} {{ invoice.currency }}</span>
						</div>

						<div class="border-t border-slate-100 pt-2 flex justify-between items-center">
							<span class="text-sm font-bold text-slate-700">{{ $t('invoices.view_balance') }}</span>
							<Badge :variant="balanceDue > 0 ? 'warning' : 'success'" class="text-sm px-2">
								{{ fmt(balanceDue) }} {{ invoice.currency }}
							</Badge>
						</div>
					</div>

					<!-- Refund action -->
					<Button
						v-if="maxRefundable > 0"
						variant="outline"
						class="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 gap-2"
						@click="isRefundModalOpen = true"
					>
						<RotateCcwIcon class="w-4 h-4" />
						{{ $t('invoices.btn_refund') }}
					</Button>
				</div>

				<!-- Timeline -->
				<div class="flex-1 overflow-y-auto p-6">
					<h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">{{ $t('invoices.timeline') }}</h4>

					<div class="space-y-6 relative before:absolute before:left-4 before:top-2 before:bottom-0 before:w-px before:bg-slate-200">
						<!-- Creation -->
						<div class="relative pl-10">
							<div class="absolute left-2 top-1 w-4 h-4 bg-blue-100 border-2 border-white ring-1 ring-blue-500 rounded-full z-10"></div>
							<div class="bg-white border border-slate-100 p-3 rounded-lg shadow-sm">
								<div class="flex justify-between items-start">
									<span class="font-medium text-slate-900 text-sm">
										{{ $t('invoices.invoice_created') }}
									</span>
									<span class="text-xs text-slate-400">{{ formatDateTime(invoice.date) }}</span>
								</div>
								<div class="text-xs text-slate-500 mt-1">
									{{ $t('invoices.status_set_to') }}
									{{ $t(`invoices.status_${invoice.status}`) }}
								</div>
							</div>
						</div>

						<!-- Transactions -->
						<div
							v-for="tx in sortedTransactions"
							:key="tx.id"
							class="relative pl-10"
						>
							<div
								class="absolute left-2 top-1 w-4 h-4 border-2 border-white ring-1 rounded-full z-10"
								:class="tx.type === TransactionType.PAYMENT ? 'bg-emerald-100 ring-emerald-500' : 'bg-red-100 ring-red-500'"
							></div>

							<div class="bg-white border border-slate-100 p-3 rounded-lg shadow-sm group hover:border-slate-300 transition-colors">
								<div class="flex justify-between items-center mb-2">
									<div class="flex items-center gap-2">
										<CreditCardIcon v-if="tx.type === TransactionType.PAYMENT" class="w-3.5 h-3.5 text-emerald-600" />
										<RotateCcwIcon v-else class="w-3.5 h-3.5 text-red-600" />

										<span
											class="font-medium text-sm"
											:class="tx.type === TransactionType.PAYMENT ? 'text-emerald-700' : 'text-red-700'"
										>
											{{ tx.type === TransactionType.PAYMENT ? $t('invoices.trans_payment') : $t('invoices.trans_refund') }}
										</span>
									</div>

									<span class="text-xs text-slate-400">{{ formatDateTime(tx.date) }}</span>
								</div>

								<div class="flex justify-between items-baseline">
									<span class="text-lg font-bold text-slate-900">
										{{ fmt(tx.amount) }}
										<span class="text-xs font-normal text-slate-500">{{ tx.currency }}</span>
									</span>
								</div>

								<div class="mt-2 pt-2 border-t border-slate-50 flex justify-between text-xs">
									<span class="text-slate-500">{{ tx.method }} {{ tx.cardMask ? `(${tx.cardMask})` : '' }}</span>
									<span class="text-slate-400 italic">{{ tx.note }}</span>
								</div>
							</div>
						</div>

					</div>
				</div>
			</div>

		</div>

		<div id="print-area">
			<div ref="pdfRef" class="w-[700px] p-8 bg-white">
				<!-- Invoice page -->
				<div class="mb-20">
					<div class="flex justify-between items-start mb-12">
						<div>
							<h1 class="text-2xl font-bold text-slate-900 mb-1">{{ invoice.senderName }}</h1>
							<div class="text-sm text-slate-500">
								<div v-for="(line, i) in senderDetailsLines" :key="i">
									{{ line }}
								</div>
							</div>
						</div>

						<div class="text-right">
							<h2 class="text-3xl font-light text-slate-900 mb-4">{{ $t('invoices.invoice_title') }}</h2>
							<div class="space-y-1 text-sm">
								<div class="flex justify-start gap-2">
									<span class="text-slate-500 font-medium w-20">{{ $t('invoices.label_number') }}:</span>
									<span class="font-bold text-slate-900">{{ invoice.number }}</span>
								</div>

								<div class="flex justify-start gap-2">
									<span class="text-slate-500 font-medium w-20">{{ $t('invoices.label_date') }}:</span>
									<span class="text-slate-900">{{ invoice.date }}</span>
								</div>

								<div class="flex justify-start gap-2">
									<span class="text-slate-500 font-medium w-20">{{ $t('invoices.label_due_date') }}:</span>
									<span class="text-slate-900">{{ formatDate(invoice.dueDate) }}</span>
								</div>
							</div>
						</div>
					</div>

					<div class="mb-12">
						<h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{{ $t('invoices.bill_to') }}</h3>
						<div class="text-slate-900 font-bold text-lg mb-1">{{ recipientName }}</div>
					</div>

					<table class="w-full text-sm">
						<thead>
							<tr class="border-b-2 border-slate-900 text-left">
								<th class="py-2 font-bold text-slate-900">{{ $t('invoices.col_description') }}</th>
								<th class="py-2 font-bold text-slate-900 text-center">{{ $t('invoices.col_qty') }}</th>
								<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.col_price') }}</th>
								<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.col_vat') }}</th>
								<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.col_total') }}</th>
							</tr>
						</thead>
						<tbody class="divide-y divide-slate-100">
							<tr v-for="it in items" :key="it.id">
								<td class="py-3 pr-4">
									<div class="font-medium text-slate-900">{{ it.name }}</div>
									<div class="text-xs text-slate-500">{{ it.extras?.code }}</div>
								</td>

								<td class="py-3 text-center">{{ it.quantity }} {{ it.extras?.unit || it.unit_code }}</td>

								<td class="py-3 text-right">{{ fmt(it.price) }}</td>

								<td class="py-3 text-right">
									{{ fmt(getLineVat(it)) }}
								</td>

								<td class="py-3 text-right font-medium">
									{{ fmt(getLineTotal(it) + fmt(getLineVat(it))) }}
								</td>
							</tr>
						</tbody>
					</table>

					<div class="flex justify-end border-t border-slate-200 pt-4">
						<div class="w-72 text-right text-lg font-bold text-slate-900">
							<span>{{ $t('invoices.view_balance') }}:</span>
							<div>
								{{ fmt(balanceDue) }} {{ invoice.currency }}
							</div>
						</div>
					</div>

				</div>

				<div v-if="sortedTransactions.length" class="print-page-break">
					<h2 class="text-2xl font-light text-slate-900 mb-8 border-b pb-4">{{ $t('invoices.hist_page_title') }}</h2>

					<table class="w-full text-sm">
						<thead>
						<tr class="border-b-2 border-slate-900 text-left">
							<th class="py-2 font-bold text-slate-900">{{ $t('invoices.hist_col_date') }}</th>
							<th class="py-2 font-bold text-slate-900">{{ $t('invoices.hist_col_type') }}</th>
							<th class="py-2 font-bold text-slate-900">{{ $t('invoices.hist_col_card') }}</th>
							<th class="py-2 font-bold text-slate-900">{{ $t('invoices.hist_col_rrn') }}</th>
							<th class="py-2 font-bold text-slate-900 text-right">{{ $t('invoices.hist_col_amount') }}</th>
						</tr>
						</thead>

						<tbody class="divide-y divide-slate-100">
						<tr v-for="(tx, idx) in sortedTransactions" :key="tx.id || idx">
							<td class="py-3">{{ tx.date }}</td>
							<td class="py-3">{{ tx.type === TransactionType.PAYMENT ? $t('invoices.trans_payment') : $t('invoices.trans_refund') }}</td>
							<td class="py-3 font-mono text-xs">{{ tx.cardMask || '-' }}</td>
							<td class="py-3 font-mono text-xs">{{ tx.rrn || tx.arn || '-' }}</td>
							<td class="py-3 text-right">{{ fmt(tx.amount) }}</td>
						</tr>
						</tbody>
					</table>
				</div>

			</div>
		</div>

		<RefundModal
			:isOpen="isRefundModalOpen"
			:currency="invoice.currency"
			:maxRefundable="maxRefundable"
			:items="invoice.items"
			@close="isRefundModalOpen = false"
			@submit="handleRefund"
		/>
	</div>
</template>

<style>
#print-area {
	position: absolute;
	top: -9999px;
	left: -9999px;
}

.print-page-break {
	page-break-before: always;
	break-before: page;
}

@media print {
	body * {
		visibility: hidden !important;
	}

	#print-area,
	#print-area * {
		visibility: visible !important;
	}

	#print-area {
		position: static !important;
		top: 0 !important;
		left: 0 !important;
	}

	body {
		background: white !important;
	}
}
</style>
